/* eslint-disable @typescript-eslint/no-empty-function */
import { FunctionComponent, PropsWithChildren, useState } from "react";

import {
  TicketingFormCategory,
  PaymentMethod,
  StripeRecurringInterval,
  getCurrencyFromCountry,
  FormType,
} from "@simplyk/common";

import { FrontendFormContext, FrontendFormContextValues } from "../../../../../common/contexts/FrontendFormContext";

import { DonationFormFrontendPageOutput } from "@/common/types/trpc";
import { useComputeTip } from "@/components/PaymentProcessor/hooks/useComputeTip";

interface DonationPreviewProviderProps {
  donationForm: DonationFormFrontendPageOutput;
}

export const PREVIEW_DISPLAYED_FORM_AMOUNT = 100_00 as const;

/**
 * This component provides dedicated instances of FrontendFormContext.Provider
 * for the live preview.
 *
 * Unlike the contexts used in normal form pages, these dedicated providers are
 * tailored for preview functionality and use mock data or placeholder values.
 */
export const DonationFrontendPreviewProvider: FunctionComponent<PropsWithChildren<DonationPreviewProviderProps>> = ({
  children,
  donationForm,
}) => {
  const organization = donationForm.organization;
  const currency = getCurrencyFromCountry(organization.country);

  const previewSelectedPaymentMethod = {
    paymentMethod: PaymentMethod.Card,
  };

  const {
    tip,
    suggestedTips,
    defaultTipSuggestion,
    selectedTip,
    isSuggestedTipsAmount,
    freeTips,
    fees,
    formattedFees,
    getTipChoice,
    tipExperiment,
  } = useComputeTip({
    displayedFormAmount: PREVIEW_DISPLAYED_FORM_AMOUNT,
    currency,
    organizationCountry: organization?.country,
    paymentMethod: previewSelectedPaymentMethod.paymentMethod,
    isAuction: false,
    category: donationForm.category,
    formData: donationForm,
    shouldFetchTipSuggestions: false,
  });

  const [stripeRecurrenceInterval, setStripeRecurrenceInterval] = useState<StripeRecurringInterval | null>(null);

  const frontendFormContextValues: FrontendFormContextValues = {
    tipExperiment,
    formData: donationForm,
    organization,
    commandId: "",
    dafFlow: null,
    setDafFlow: () => {},
    formType: FormType.Ticketing,
    category: TicketingFormCategory.Event,
    translationLocales: [],
    isOnboarding: false,
    isEmbed: false,
    isEmbedV2: false,
    formEmbedVersion: null,
    embedId: null,
    formAmplitudeContext: {} as never,
    selectedPaymentMethod: undefined,
    setSelectedPaymentMethod: () => {},
    currency,
    displayedFormAmount: PREVIEW_DISPLAYED_FORM_AMOUNT,
    setDisplayedFormAmount: () => {},
    displayedFormTaxAmount: 0,
    setDisplayedFormTaxAmount: () => {},
    tipBaseAmount: PREVIEW_DISPLAYED_FORM_AMOUNT,
    setShouldFetchTipSuggestions: () => {},
    displayedFormAmountWithTip: PREVIEW_DISPLAYED_FORM_AMOUNT + tip,
    tip,
    suggestedTips,
    defaultTipSuggestion,
    selectedTip,
    isSuggestedTipsAmount,
    handleChangeTips: () => {},
    handleChangeFreeTips: () => {},
    freeTips,
    updateFreeTipAmount: () => {},
    fees,
    formattedFees,
    displayTipSelection: true,
    stripeRecurrenceInterval,
    setStripeRecurrenceInterval,
    isMembershipV2: false,
    isArchived: false,
    areBankPaymentMethodsAllowed: false,
    allowedPaymentMethods: [],
    postTransactionUrl: null,
    prioritizeBankPayment: false,
    paymentStepExperiment: null,
    markAmountChangedByDonor: () => undefined,
    selectedPaymentMethodIsBank: false,
    selectedPaymentMethodIsCard: false,
    chequeDescription: null,
    showProceedButton: false,
    displayExpressCheckout: false,
    isCashAppEnabled: false,
    supportsExpressCheckout: false,
    handleSetSupportsExpressCheckout: () => {},
    isExpressCheckoutLoading: false,
    isSubmitting: false,
    setIsSubmitting: () => {},
    expressCheckoutIsSelected: false,
    expressCheckoutDisabled: false,
    paymentMode: "payment" as const,
    isAuction: false,
    shouldSendTipPercentage: false,
    getTipChoice,
    logAmplitudeTipsChanged: () => {},
    displayAddressQuestion: false,
    isPreviewTemplateMode: false,
    isPreview: true,
    generateETicket: false,
    defaultSelectedPaymentMethod: previewSelectedPaymentMethod.paymentMethod,
    isFreeTicketing: false,
    isPaymentDisabled: false,
    isPadLoading: false,
    setIsPadLoading: () => {},
  };

  return <FrontendFormContext.Provider value={frontendFormContextValues}>{children}</FrontendFormContext.Provider>;
};
